import { apiClient } from './apiClient';
import { EnrichedUser, UsersResponse } from '../types/auth';
import toast from 'react-hot-toast';
import { captureHandledError } from '../utilities/datadog';

// Get all users
// api/users.ts
export const getUsers = async (
  orgId?: string | number
): Promise<UsersResponse | null> => {
  try {
    const url = orgId ? `/users?org_id=${orgId}` : `/users`;
    const res = await apiClient(url, { method: 'GET' });
    if (!res.ok) {
      const errorData = await res.json();
      captureHandledError('Failed to fetch users', errorData, { orgId });
      return null;
    }
    const data: UsersResponse = await res.json();
    return data;
  } catch (err) {
    captureHandledError('Error fetching users', err, { orgId });
    return null;
  }
};

// Get user by ID
export const getUser = async (
  user_id?: string
): Promise<EnrichedUser | null> => {
  try {
    const params = new URLSearchParams();
    if (user_id) params.append('user_id', String(user_id));

    const url = params.toString()
      ? `/users/me?${params.toString()}`
      : `/users/me`;
    const response = await apiClient(url, {
      method: 'GET',
    });

    if (response.ok) {
      const data: EnrichedUser = await response.json();
      return data;
    } else {
      const errorData = await response.json();
      captureHandledError('Failed to fetch user', errorData, { user_id });
      return null;
    }
  } catch (error) {
    captureHandledError('Error fetching user', error, { user_id });
    return null;
  }
};

// update User org_id

export const updateUserOrgId = async (
  user: EnrichedUser
): Promise<EnrichedUser | null> => {
  try {
    // change user object keys to snake_case
    const formattedUser = {
      first_name: user.firstName,
      last_name: user.lastName,
      email: user.email,
      phone_number: user.phoneNumber,
      organization_role: user.organizationRole,
      org_id: user.org_id,
    };

    const response = await apiClient(`/users/me`, {
      method: 'PATCH',
      body: JSON.stringify(formattedUser),
    });

    if (response.ok) {
      const data: EnrichedUser = await response.json();
      return data;
    } else {
      const errorData = await response.json();
      captureHandledError('Failed to update user org_id', errorData);
      return null;
    }
  } catch (error) {
    captureHandledError('Error updating user org_id', error);
    return null;
  }
};

// Update user profile information
type ApiUserPatch = Partial<{
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string | null;
  organization_role: string;
  deleted_at: string | null;
  deleted_by: string | null;
  is_email_signer: boolean;
  email_signature: string;
}>;
export async function updateUser(
  user_id: string,
  patch: ApiUserPatch
): Promise<EnrichedUser | null> {
  const payload = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined)
  );
  if (Object.keys(payload).length === 0) return null;

  try {
    const response = await apiClient(`/users/me`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || 'Failed to update user.';
      toast.error(errorMessage);
      captureHandledError(
        `updateUser failed: ${response.status} ${response.statusText}`,
        errorData,
        { user_id }
      );
      return null;
    }
    return response.json();
  } catch (err: unknown) {
    // Handle validation errors that might be objects
    let errorMessage = 'Failed to update user.';
    const errMessage = err instanceof Error ? err.message : '';
    if (errMessage) {
      try {
        const parsed = JSON.parse(errMessage);
        errorMessage = parsed.detail || parsed.message || errorMessage;
      } catch {
        errorMessage = errMessage;
      }
    }
    toast.error(errorMessage);
    captureHandledError('updateUser error', err, { user_id });
    return null;
  }
}

export async function uploadMyProfileImage(
  file: File
): Promise<EnrichedUser | null> {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await apiClient('/users/me/image', {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      // apiClient already toasts on !ok and throws, don't toast again
      const errorData = await response.json().catch(() => ({}));
      captureHandledError(
        `uploadMyProfileImage failed: ${response.status} ${response.statusText}`,
        errorData
      );
      return null;
    }
    return response.json();
  } catch (err: unknown) {
    captureHandledError('uploadMyProfileImage error', err);
    return null;
  }
}

// Update a specific (target) user by admin; requires ADMIN permission server-side.
export async function updateTargetUser(
  user_id: string,
  patch: ApiUserPatch
): Promise<EnrichedUser | null> {
  const payload = Object.fromEntries(
    Object.entries(patch).filter(([, v]) => v !== undefined)
  );
  if (Object.keys(payload).length === 0) return null;

  try {
    const response = await apiClient(`/users?user_id=${user_id}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || 'Failed to update user.';
      toast.error(errorMessage);
      captureHandledError(
        `updateTargetUser failed: ${response.status} ${response.statusText}`,
        errorData,
        { user_id }
      );
      return null;
    }
    return response.json();
  } catch (err: unknown) {
    let errorMessage = 'Failed to update user.';
    const errMessage = err instanceof Error ? err.message : '';
    if (errMessage) {
      try {
        const parsed = JSON.parse(errMessage);
        errorMessage = parsed.detail || parsed.message || errorMessage;
      } catch {
        errorMessage = errMessage;
      }
    }
    toast.error(errorMessage);
    captureHandledError('updateTargetUser error', err, { user_id });
    return null;
  }
}

export async function createUser(
  request: ApiUserPatch
): Promise<EnrichedUser | null> {
  const payload = Object.fromEntries(
    Object.entries(request).filter(([, v]) => v !== undefined)
  );
  if (Object.keys(payload).length === 0) return null;

  const response = await apiClient(`/users`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    toast.error('Failed to create a user.');
    throw new Error(
      `Error creating a user: ${response.status} ${response.statusText}`
    );
  }
  toast.success('User was successfully added!');
  return response.json();
}

// Create a user and send invitation email (POST /users with send_invite=true)
type UserInviteRequest = {
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string | null;
  organization_role: string;
  org_id: string;
};

export async function inviteUser(
  request: UserInviteRequest
): Promise<EnrichedUser | null> {
  try {
    const response = await apiClient(`/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...request, send_invite: true }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || 'Failed to send invitation.';
      toast.error(errorMessage);
      captureHandledError(
        `inviteUser failed: ${response.status} ${response.statusText}`,
        errorData
      );
      return null;
    }
    toast.success('Invitation email sent successfully!');
    return response.json();
  } catch (err: unknown) {
    let errorMessage = 'Failed to send invitation.';
    const errMessage = err instanceof Error ? err.message : '';
    if (errMessage) {
      try {
        const parsed = JSON.parse(errMessage);
        errorMessage = parsed.detail || parsed.message || errorMessage;
      } catch {
        errorMessage = errMessage;
      }
    }
    toast.error(errorMessage);
    captureHandledError('inviteUser error', err);
    return null;
  }
}

// Change user password
type PasswordChangeRequest = {
  current_password: string;
  new_password: string;
};

export async function changePassword(
  passwordData: PasswordChangeRequest
): Promise<{ message: string } | null> {
  try {
    const response = await apiClient(`/users/me/password`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(passwordData),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      const errorMessage =
        errorData.detail || errorData.message || 'Failed to change password.';
      toast.error(errorMessage);
      captureHandledError(
        `changePassword failed: ${response.status} ${response.statusText}`,
        errorData
      );
      return null;
    }
    return response.json();
  } catch (err: unknown) {
    // Handle validation errors that might be objects
    let errorMessage = 'Failed to change password.';
    const errMessage = err instanceof Error ? err.message : '';
    if (errMessage) {
      try {
        const parsed = JSON.parse(errMessage);
        errorMessage = parsed.detail || parsed.message || errorMessage;
      } catch {
        errorMessage = errMessage;
      }
    }
    toast.error(errorMessage);
    captureHandledError('changePassword error', err);
    return null;
  }
}
